import Vue from "vue";
import VueRouter from "vue-router";
import { colors, QSpinnerFacebook, SessionStorage } from "quasar";
import { version } from "../../package.json";
import routes from "./routes";
import { TokenManager } from '@cenora-llc/cenora-internal-communication-library';

Vue.use(VueRouter);

function applyBranding(config) {
  const branding = config.branding;
  const cdnUrl = branding.cdnUrl;

  // Apply colors
  colors.setBrand("primary", branding.colors.secondary);
  colors.setBrand("secondary", branding.colors.primary);
  colors.setBrand("accent", branding.colors.accent);
  colors.setBrand("negative", branding.colors.negative);
  colors.setBrand("positive", branding.colors.positive);
  colors.setBrand("dark", branding.colors.dark);
  colors.setBrand("info", branding.colors.info);
  colors.setBrand("warning", branding.colors.warning);

  // Apply fonts
  document.documentElement.style.setProperty(
    "--bodyFontFamily",
    branding.fonts.body.fontStyle.fontFamily
  );
  document.documentElement.style.setProperty(
    "--bodyFontStyle",
    branding.fonts.body.fontStyle.fontStyle
  );
  document.documentElement.style.setProperty(
    "--bodyFontWeight",
    branding.fonts.body.fontStyle.fontWeight
  );
  document.documentElement.style.setProperty(
    "--bodyFontSize",
    branding.fonts.body.fontStyle.fontSize
  );

  // Apply favicon
  const favicon = document.getElementById("favicon");
  if (favicon) {
    favicon.type = branding.metas.link.favicon_16x16.type;
    favicon.href = cdnUrl + branding.metas.link.favicon_16x16.href;
  }
}

async function loadConfigAndBranding(store, token) {
  await store.dispatch("auth/config", { token });
  applyBranding(store.state.auth.config);
}

export default function ({ store, Vue }) {
  const Router = new VueRouter({
    scrollBehavior: () => ({ x: 0, y: 0 }),
    routes,
    mode: process.env.VUE_ROUTER_MODE,
    base: process.env.VUE_ROUTER_BASE
  });

  Router.beforeEach(async (to, from, next) => {
    const path = to.path;
    const query = to.query;

    // Actualiza labels dinámicos en breadcrumbs
    if (to.meta.breadcrumb) {
      for (const breadcrumb of to.meta.breadcrumb) {
        if (typeof breadcrumb.metaLabel === 'function') {
          breadcrumb.label = breadcrumb.metaLabel(to);
        }
      }
    }

    // Load config if not already loaded
    if (!store.state.auth.config) {
      console.info("Build Version: ", version);

      // Check login with token param ?c=
      if (path === "/" && query.c) {
        let loggedIn = null;
        try {
          const decodedString = atob(query.c);
          const jsonResult = JSON.parse(decodedString);

          // PRIORITY 1: Request token from parent via TokenManager (WAIT for it to arrive)
          let token = null;
          if (TokenManager) {
            try {
              console.log('🔄 [Router] Requesting token from parent via TokenManager...');
              token = await TokenManager.getToken();
              if (token) {
                console.log('✅ [Router] Received token from parent via TokenManager');
              }
            } catch (error) {
              console.warn('⚠️ [Router] Failed to get token from TokenManager:', error);
            }
          }

          if (!token) {
            // PRIORITY 2: Fallback to URL parameter token (backward compatibility)
            token = jsonResult.token;
            if (token) {
              console.log('⚠️ [Router] Using token from URL parameter (TokenManager unavailable or failed)');
            } else {
              throw new Error("Token not found from TokenManager or URL parameter");
            }
          }

          await loadConfigAndBranding(store, token);
          loggedIn = await tokenLogin(Vue.prototype, token, store.state.auth.config.userpool, store, jsonResult.user);

        } catch (e) {
          console.error("Error decoding token", e);
        }

        if (loggedIn) {
          console.log('Token login successful');

          if (store.state.auth.hasSuperset) {
            next("/st");
          } else {
            next("/portal");
          }

        } else {
          console.error('Token login unsuccessful');
          next({ path: "/logout" });
        }
        return;

      } else {
        // Try to load config with existing token from SessionStorage
        const existingToken = SessionStorage.getItem('token');
        if (existingToken) {
          try {
            await loadConfigAndBranding(store, existingToken);
          } catch (e) {
            console.error('Failed to load config with existing token', e);
            SessionStorage.clear();
            next({ path: "/logout" });
            return;
          }
        }
      }
    }

    // Protected routes check
    if (path.indexOf("/portal/") !== -1 || path === "/portal") {
      if (!isAuthorized()) {
        next({ path: "/logout" });
      } else {
        next();
      }

    } else if (path.indexOf("/st") !== -1 || path === "/st") {
      if (!isAuthorized()) {
        next({ path: "/logout" });
      } else {
        next();
      }

    } else {
      next();
    }
  });

  patchRouterMethod(Router, "push");
  patchRouterMethod(Router, "replace");

  return Router;
}

async function tokenLogin(_this, token, userPool, store, userFromUrl = null) {
  _this.$q.loading.show({
    spinner: QSpinnerFacebook,
    spinnerColor: "white",
    spinnerSize: 140,
    backgroundColor: "primary",
    message: "Logging in...",
    messageColor: "white"
  });

  try {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid token format');
    const payload = JSON.parse(atob(parts[1]));

    if (payload.exp && payload.exp * 1000 < Date.now()) {
      throw new Error('Token expired');
    }

    const hasSuperset = payload["custom:hasSuperset"] === "true";
    store.commit("auth/setHasSuperset", hasSuperset);
    const roles = (payload['custom:roles'] || '').split('|').filter(Boolean);
    store.commit("auth/setRoles", roles);

    const userObj = {
      first_name: payload.given_name,
      last_name: payload.family_name,
      username: payload.nickname
    };
    if (userFromUrl?.cognito_id) {
      userObj.cognito_id = userFromUrl.cognito_id;
    }
    SessionStorage.set("user", userObj);
    SessionStorage.set("token", token);

    _this.$q.loading.hide();
    return true;

  } catch (e) {
    _this.$q.loading.hide();
    console.error("Invalid token", e);
    return false;
  }
}

function isAuthorized() {
  return SessionStorage.has("token");
}

function patchRouterMethod(router, methodName) {
  router["old" + methodName] = router[methodName];
  router[methodName] = async function (location) {
    return router["old" + methodName](location).catch(error => {
      if (error === undefined || error.name === "NavigationDuplicated") {
        return this.currentRoute;
      }
      throw error;
    });
  };
}
